import {exhaustive} from '@freckle/exhaustive'
import {groupBy} from '@freckle/educator-entities/ts/common/helpers/map'
import type {TranslateT} from '@freckle/educator-entities/ts/common/helpers/translate'
import type {StudentGoalT} from '@freckle/educator-entities/ts/common/models/student-goal'
import type {StudentAttrs} from '@freckle/educator-entities/ts/users/models/student'
import {Table, cellFromRow} from '@freckle/educator-materials/ts/components/table'
import {TriSectionProgressBar} from '@freckle/educator-materials/ts/components/trisection-progress-bar'
import {useSafeTranslation} from '@freckle/educator-materials/ts/helpers/translate'
import {
  badStyle,
  goodStyle,
  neutralStyle,
  notAvailableStyle,
  completeStyle,
  incompleteStyle,
} from './student-goals-table.module.scss'

type Props = {
  goals: Array<StudentGoalT>
  students: Array<StudentAttrs>
}

type TableDatumT = {
  firstName: string
  lastName: string
  description: string
  percentComplete: number | null | undefined
}

export function StudentGoalsTable({students, goals}: Props) {
  const {t} = useSafeTranslation()

  const tableData = rightJoinStudentGoals(t, students, goals)

  return (
    <Table
      data={tableData}
      columns={[
        {
          Header: t('STUDENT_GOAL_TABLE_FIRST_NAME_COLUMN_HEADER'),
          accessor: d => d.firstName,
          id: 'first-name',
          alignment: 'left',
        },
        {
          Header: t('STUDENT_GOAL_TABLE_LAST_NAME_COLUMN_HEADER'),
          accessor: d => d.lastName,
          id: 'last-name',
          alignment: 'left',
          defaultSortOrder: 'desc',
        },
        {
          Header: t('STUDENT_GOAL_TABLE_GOAL_COLUMN_HEADER'),
          accessor: d => d.description,
          id: 'goal',
          alignment: 'left',
        },
        {
          Header: t('STUDENT_GOAL_TABLE_STATUS_COLUMN_HEADER'),
          Cell: cellFromRow(({percentComplete}) => (
            <PercentComplete percentComplete={percentComplete} />
          )),
          accessor: d => d.percentComplete,
          id: 'percent-complete',
          alignment: 'left',
        },
      ]}
    />
  )
}

function rightJoinStudentGoals(
  t: TranslateT,
  students: Array<StudentAttrs>,
  goals: Array<StudentGoalT>,
): Array<TableDatumT> {
  const goalsByStudentId = groupBy(goals, x => x.studentId)

  return students.flatMap(student => {
    const studentGoals = goalsByStudentId.get(student.id)
    if (studentGoals !== undefined) {
      return studentGoals.flatMap((studentGoal): TableDatumT[] =>
        // This metric _might_ come back from the API but we don't support it
        studentGoal.metric === 'levels'
          ? []
          : [
              {
                firstName: student.firstName,
                lastName: student.lastName,
                description: describeGoal(t, studentGoal.metric, studentGoal.value),
                percentComplete: studentGoal.percentComplete,
              },
            ],
      )
    } else {
      return [
        {
          firstName: student.firstName,
          lastName: student.lastName,
          description: t('STUDENT_GOAL_TABLE_NO_GOAL_DESCRIPTION'),
          percentComplete: null,
        } as TableDatumT,
      ]
    }
  })
}

function describeGoal(
  t: TranslateT,
  metric: 'accuracy' | 'correctQuestions',
  numGoal: number,
): string {
  switch (metric) {
    case 'accuracy':
      return t('STUDENT_GOAL_TABLE_DESCRIBE_ACCURACY_GOAL', {numGoal}) //`Get above 80% accuracy ${numGoal} times`
    case 'correctQuestions':
      return t('STUDENT_GOAL_TABLE_DESCRIBE_CORRECT_QUESTIONS_GOAL', {numGoal}) //`Get above ${numGoal} correct questions`
    default:
      return exhaustive(metric, 'StudentGoalMetricT')
  }
}

function percentageCompleteClass(percent: number): string {
  if (percent < 50) {
    return badStyle
  } else if (percent >= 80) {
    return goodStyle
  } else {
    return neutralStyle
  }
}

function PercentComplete({percentComplete}: {percentComplete: number | null | undefined}) {
  const {t} = useSafeTranslation()

  if (percentComplete === null || percentComplete === undefined) {
    return (
      <span className={notAvailableStyle}>
        {t('STUDENT_GOAL_TABLE_FORMAT_NO_PERCENT_COMPLETE')}
      </span>
    )
  } else if (percentComplete >= 100) {
    return (
      <span className={completeStyle}>
        {t('STUDENT_GOAL_TABLE_FORMAT_COMPLETED_PERCENT_COMPLETE')}
      </span>
    )
  } else {
    return (
      <TriSectionProgressBar
        leftStat={percentComplete}
        middleStat={100 - percentComplete}
        rightStat={0}
        leftPopoverText={`${percentComplete}%`}
        middlePopoverText={t('STUDENT_GOAL_TABLE_FORMAT_INCOMPLETE_PERCENT_COMPLETE')}
        rightPopoverText=""
        leftClassName={percentageCompleteClass(percentComplete)}
        middleClassName={incompleteStyle}
        rightClassName=""
      />
    )
  }
}
